import { memo } from 'react';
import isEqual from 'react-fast-compare';
import styled, { css } from 'styled-components';

import { PriceTax } from './PriceTax';
import { PriceDisplayMode } from '~/utilities/graphql/codegen';
import type { Price, PriceDisplayConfiguration } from '~/utilities/graphql/codegen';
import { useMembersOnlyPrices } from '~/utilities/hooks/useMembersOnlyPrices';

const S = {
  ProductPriceLegoWrapper: styled.div`
    display: flex;
    flex-direction: column;

    &[data-show-in-one-row='true'] {
      flex-direction: row-reverse;
      justify-content: start;
      align-items: center;
      gap: 2px;
    }
  `,

  Price: styled.div`
    display: flex;
    align-items: flex-end;
    gap: ${({ theme }) => theme.padding['3xs']};
  `,
  PriceValue: styled.strong`
    ${({ theme }) => css`
      font-weight: ${theme.variables.font.weight.medium};
      font-size: ${theme.font.size[3]};

      &[data-has-discounted-price='true'] {
        color: var(--base-price-color, ${theme.colors.content.sale});
        font-weight: ${theme.variables.font.weight.bold};
        font-size: ${theme.font.size[3]};
      }
    `}
  `,

  ItpPrice: styled.div`
    font-size: ${({ theme }) => theme.font.size[3]};
    text-decoration: line-through;
  `,

  RRPPrice: styled.div`
    ${({ theme }) => css`
      color: ${theme.colors.content.primary};
      display: flex;
      gap: ${theme.padding['3xs']};
      font-size: ${theme.font.size[2]};

      &[data-show-in-one-row='true'] {
        font-size: ${theme.font.size[3]};
        flex-direction: row-reverse;
      }
    `}
  `,
  RRPPriceValue: styled.span`
    &[data-rrp-strikethrough='true'] {
      text-decoration: line-through;
    }
  `,
  RRPText: styled.span`
    ${({ theme }) => css`
      font-style: normal;
      font-weight: ${theme.variables.font.weight.regular};
      white-space: nowrap;
      vertical-align: bottom;
    `}
  `,

  L30DPrice: styled.div`
    ${({ theme }) => css`
      color: ${theme.colors.content.primary};
      display: flex;
      gap: ${theme.padding['3xs']};
      font-size: ${theme.font.size[2]};
    `}
  `,
  L30DPriceValue: styled.span`
    &[data-l30d-strikethrough='true'] {
      text-decoration: line-through;
    }
  `,
  L30DPriceText: styled.span``,
};

type Props = {
  taxV2?: string;
  price?: Price | null;
  personalisedPrice?: Price | null;
  fromPrice?: Price | null;
  intermediaryPrice?: Price | null;
  formattedLowestPrice?: string | null;
  basePriceColor?: string;
  priceDisplayConfig: PriceDisplayConfiguration;
  showInOneRow: boolean;
};

const ProductPriceConstructorComponent = ({
  taxV2 = '',
  price,
  personalisedPrice,
  fromPrice,
  intermediaryPrice,
  formattedLowestPrice,
  basePriceColor,
  priceDisplayConfig,
  showInOneRow = false,
}: Props) => {
  const { priceFormatted, priceValue } = useMembersOnlyPrices({
    price,
    personalisedPrice,
  });

  const {
    rrpDisplay,
    rrpStrikethrough,
    rrpText,
    l30dDisplay,
    l30dStrikethrough,
    l30dText1,
    l30dText2,
    itpDisplay,
    displayMode,
  } = priceDisplayConfig || {};

  const l30dText = l30dText1 || l30dText2;
  const showRrpValue = rrpDisplay && fromPrice?.formattedValue;
  const showL30dValue =
    l30dDisplay &&
    (formattedLowestPrice ||
      (fromPrice?.formattedValue && displayMode === PriceDisplayMode.L30D_2));
  const hasDiscountPrice =
    !!fromPrice?.formattedValue && (fromPrice?.value || 0) > (priceValue || 0);

  if (!priceFormatted) {
    return <></>;
  }

  return (
    <S.ProductPriceLegoWrapper data-show-in-one-row={showInOneRow} data-testid="price">
      <S.Price>
        <S.PriceValue
          style={basePriceColor ? { '--base-price-color': basePriceColor } : undefined}
          data-testid="summary-product-price"
          data-cs-capture=""
          data-has-discounted-price={hasDiscountPrice}
        >
          {priceFormatted}
        </S.PriceValue>
        <PriceTax taxV2={taxV2} />
      </S.Price>

      {itpDisplay && intermediaryPrice?.formattedValue && (
        <S.ItpPrice data-testid="summary-intermediary-price">
          {intermediaryPrice?.formattedValue}
        </S.ItpPrice>
      )}

      {(showRrpValue || rrpText) && (
        <S.RRPPrice data-show-in-one-row={showInOneRow}>
          {showRrpValue && (
            <S.RRPPriceValue
              data-testid="summary-before-price"
              data-cs-capture=""
              data-rrp-strikethrough={rrpStrikethrough}
            >
              {fromPrice?.formattedValue}
            </S.RRPPriceValue>
          )}
          {rrpText && <S.RRPText>{rrpText}</S.RRPText>}
        </S.RRPPrice>
      )}

      {(showL30dValue || l30dText) && (
        <S.L30DPrice data-testid="lowest-price-text" data-cs-capture="">
          {showL30dValue && (
            <S.L30DPriceValue data-l30d-strikethrough={l30dStrikethrough}>
              {formattedLowestPrice || fromPrice?.formattedValue}
            </S.L30DPriceValue>
          )}
          {l30dText && <S.L30DPriceText>{l30dText}</S.L30DPriceText>}
        </S.L30DPrice>
      )}
    </S.ProductPriceLegoWrapper>
  );
};

export const ProductPriceConstructor = memo(ProductPriceConstructorComponent, isEqual);
